import * as React from 'react';
import {FunctionComponent, useState} from 'react';
import {classNames} from '../../../../Support/classNames';
import {ReportData} from '../TrendReportPresenter';
import {formatAverageSplit} from '../../../../Support/Formatting/formatSplit';
import {Speed} from '../../../../../Domain/Speed';
import {formatAveragePower} from '../../../../Support/Formatting/formatPower';
import {format} from 'date-fns';
import {Graph} from '../Components/Graph';
import {Duration} from '../../../../../Domain/Duration';
import {ReportRowType, TableReportRow, ReportTemplate, TrendTemplate} from '../Models/ReportTemplate';
import {UserWithSnippets} from './Internal/UserWithSnippets';
import {Table} from './Table';
import {TemplateGraph} from './TemplateGraph';
import Modal from '../../../../Components/Modal';
import {TrendTemplateForm} from '../Forms/TrendTemplateForm';

interface OwnProps {
    index: number;
    isLocked: boolean;
    reportData: ReportData;
    template: TrendTemplate;
    onChange: (template: TrendTemplate) => void;
    onDelete: () => void;
    onDeleteSnippet: (selectedSnippetIds: number) => void;
    selectedSnippetIds: number[];
    onChangeSelectedSnippetIds: (selectedSnippetIds: number[]) => void;
    highlightedSnippetIds: number[];
    onChangeHighlightedSnippetIds: (highlightedSnippetIds: number[]) => void;
    className: string;
}

export const MainTable: FunctionComponent<OwnProps> = ({
    index,
    isLocked,
    reportData,
    template,
    onChange,
    onDelete,
    onDeleteSnippet,
    selectedSnippetIds,
    onChangeSelectedSnippetIds,
    highlightedSnippetIds,
    onChangeHighlightedSnippetIds,
    className,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const usersWithSnippets = reportData.snippets.reduce((acc: UserWithSnippets[], snippet) => {
        if (
            snippet.publicWorkout.publicUser &&
            !acc.some((userWithSnippets) => userWithSnippets.publicUser.id === snippet.publicWorkout.publicUser!.id)
        ) {
            return [
                ...acc,
                {
                    publicUser: snippet.publicWorkout.publicUser,
                    snippets: reportData.snippets
                        .filter((s) => s.publicWorkout.publicUser?.id === snippet.publicWorkout.publicUser?.id)
                        .sort((a, b) => a.publicWorkout.executedAt.getTime() - b.publicWorkout.executedAt.getTime()),
                },
            ];
        }
        return acc;
    }, []);

    const numDataColumns = Math.max(
        1,
        ...template.rows
            .filter((row): row is TableReportRow => row.type === ReportRowType.Table)
            .map((row) => row.fields.length),
    );

    return (
        <div className={classNames('row', className)}>
            <div className="col-12">
                <h2 className="d-flex justify-content-between">
                    <span>{template.title || 'Comparison ' + (index + 1)}</span>
                    <div className="d-flex">
                        {!isLocked && (
                            <button className="btn btn-outline-secondary" onClick={() => setIsEditing(true)}>
                                Edit trend
                            </button>
                        )}
                    </div>
                </h2>
            </div>
            <div className="col-12">
                <div className="card progresstable">
                    <table className="table">
                        <thead>
                            <tr>
                                {usersWithSnippets.map((userWithSnippet) => (
                                    <th
                                        key={userWithSnippet.publicUser.id}
                                        colSpan={numDataColumns}
                                        className="progresstable__header border-end border-2"
                                    >
                                        {userWithSnippet.publicUser.displayName}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {template.rows.map((row, i) => {
                                switch (row.type) {
                                    case ReportRowType.Table:
                                        return (
                                            <Table
                                                key={i}
                                                numDataColumns={numDataColumns}
                                                template={row}
                                                usersWithSnippets={usersWithSnippets}
                                                selectedSnippetIds={selectedSnippetIds}
                                                onChangeSelectedSnippetIds={onChangeSelectedSnippetIds}
                                                onChangeHighlightedSnippetIds={onChangeHighlightedSnippetIds}
                                                onDeleteSnippet={(snippetId) => {onDeleteSnippet(snippetId)}}
                                            />
                                        );
                                    case ReportRowType.Graph:
                                        return (
                                            <TemplateGraph
                                                key={i}
                                                numDataColumns={numDataColumns}
                                                template={row}
                                                usersWithSnippets={usersWithSnippets}
                                                highlightedSnippetIds={highlightedSnippetIds}
                                            />
                                        );
                                }
                            })}
                        </tbody>
                    </table>

                    <Modal visible={isEditing} wide onHide={() => setIsEditing(false)}>
                        <TrendTemplateForm
                            onFinish={(updatedTemplate) => {
                                setIsEditing(false);
                                onChange(updatedTemplate);
                            }}
                            onDelete={() => {
                                setIsEditing(false);
                                onDelete();
                            }}
                            onCancel={() => setIsEditing(false)}
                            trendTemplate={template}
                        />
                    </Modal>
                </div>
            </div>
        </div>
    );
};
